// src/redux/Slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../Url/BaseUrl';

// --- Helper Functions ---

// Function to decode the JWT token
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Function to check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;
    const expiryTime = decoded.exp * 1000;
    return Date.now() >= expiryTime;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};

// Get token expiry time in milliseconds
export const getTokenExpiryTime = (token) => {
  if (!token) return null;
  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return null;
    return decoded.exp * 1000;
  } catch (error) {
    console.error('Error getting token expiry:', error);
    return null;
  }
};

// Function to handle storing user data with expiry
const storeUserData = (token, decodedToken) => {
  const userRole = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 
                   decodedToken.role || 
                   decodedToken['role'] || 
                   'user';
  
  const expiryTime = decodedToken.exp ? decodedToken.exp * 1000 : null;
  
  localStorage.setItem('token', token);
  localStorage.setItem('userEmail', decodedToken.sub || '');
  localStorage.setItem('userRole', userRole);
  localStorage.setItem('userId', decodedToken.uid || decodedToken.sub || '');
  localStorage.setItem('userName', `${decodedToken.given_name || ''} ${decodedToken.family_name || ''}`.trim());
  
  // Store expiry time
  if (expiryTime) {
    localStorage.setItem('tokenExpiry', expiryTime.toString());
  }
  
  sessionStorage.setItem('isAuthenticated', 'true');
  sessionStorage.setItem('userRole', userRole);

  return { 
    token, 
    userRole, 
    email: decodedToken.sub || '', 
    userId: decodedToken.uid || decodedToken.sub || '',
    userName: `${decodedToken.given_name || ''} ${decodedToken.family_name || ''}`.trim(),
    expiryTime
  };
};

// --- Async Thunks for API Calls ---

// Thunk for handling Login
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async (loginData, { rejectWithValue }) => {
    try {
      const response = await axios.post(BaseUrl.baseurl + 'Auth/login', loginData);
      
      if (response.data.requiresOtp) {
        return {
          requiresOtp: true,
          userId: response.data.userId,
          message: response.data.message || 'OTP sent to registered email'
        };
      }
      
      const { token } = response.data;

      if (!token) {
        return rejectWithValue('Login failed. No token received.');
      }
      
      const decodedToken = decodeToken(token);
      if (!decodedToken) {
        return rejectWithValue('Invalid token received.');
      }

      const userData = storeUserData(token, decodedToken);
      return userData;

    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Login failed';
      return rejectWithValue(message);
    }
  }
);

// Thunk for handling OTP Verification
export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ email, password, otp }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BaseUrl.baseurl}Auth/verify-otp?otp=${otp}`,
        { email, password }
      );
      
      const { token } = response.data;

      if (!token) {
        return rejectWithValue('Verification failed. No token received.');
      }
      
      const decodedToken = decodeToken(token);
      if (!decodedToken) {
        return rejectWithValue('Invalid token received.');
      }

      const userData = storeUserData(token, decodedToken);
      return userData;

    } catch (err) {
      const message = err.response?.data?.message || err.message || 'OTP verification failed';
      return rejectWithValue(message);
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async (registerData, { rejectWithValue }) => {
    try {
      const api = axios.create({
        baseURL: BaseUrl.baseurl,
        headers: {
          'Content-Type': 'application/json',
          'accept': '*/*'
        }
      });
      
      const response = await api.post('Auth/register', registerData);

      if (response.status === 200 || response.status === 201) {
        return response.data;
      }
      
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Registration failed';
      return rejectWithValue(message);
    }
  }
);

// --- Auth Slice ---

const getInitialState = () => {
  const token = localStorage.getItem('token');
  const tokenExpiry = localStorage.getItem('tokenExpiry');
  const isExpired = tokenExpiry ? Date.now() >= parseInt(tokenExpiry) : true;
  
  if (token && isExpired) {
    localStorage.clear();
    sessionStorage.clear();
    return {
      token: null,
      user: {
        email: null,
        role: null,
        userId: null,
        userName: null,
      },
      isAuthenticated: false,
      loading: false,
      error: null,
      success: null,
      tokenExpiry: null,
    };
  }

  return {
    token: token || null,
    user: {
      email: localStorage.getItem('userEmail') || null,
      role: localStorage.getItem('userRole') || null,
      userId: localStorage.getItem('userId') || null,
      userName: localStorage.getItem('userName') || null,
    },
    isAuthenticated: !!token && !isExpired,
    loading: false,
    error: null,
    success: null,
    tokenExpiry: tokenExpiry ? parseInt(tokenExpiry) : null,
  };
};

const initialState = getInitialState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthMessages: (state) => {
      state.error = null;
      state.success = null;
    },
    logout: (state) => {
      localStorage.clear();
      sessionStorage.clear();
      state.token = null;
      state.user = { email: null, role: null, userId: null, userName: null };
      state.isAuthenticated = false;
      state.error = null;
      state.success = 'Logged out successfully.';
      state.tokenExpiry = null;
    },
    checkTokenExpiry: (state) => {
      const token = localStorage.getItem('token');
      const tokenExpiry = localStorage.getItem('tokenExpiry');
      
      if (!token || !tokenExpiry) {
        state.isAuthenticated = false;
        state.token = null;
        state.user = { email: null, role: null, userId: null, userName: null };
        return;
      }
      
      const isExpired = Date.now() >= parseInt(tokenExpiry);
      if (isExpired) {
        localStorage.clear();
        sessionStorage.clear();
        state.token = null;
        state.user = { email: null, role: null, userId: null, userName: null };
        state.isAuthenticated = false;
        state.tokenExpiry = null;
        state.error = 'Session expired. Please login again.';
      }
    },
    refreshTokenExpiry: (state) => {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = decodeToken(token);
        if (decoded && decoded.exp) {
          const expiryTime = decoded.exp * 1000;
          state.tokenExpiry = expiryTime;
          localStorage.setItem('tokenExpiry', expiryTime.toString());
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        
        if (action.payload.requiresOtp) {
          state.success = action.payload.message;
          return;
        }
        
        state.token = action.payload.token;
        state.user = { 
          email: action.payload.email, 
          role: action.payload.userRole, 
          userId: action.payload.userId,
          userName: action.payload.userName,
        };
        state.isAuthenticated = true;
        state.tokenExpiry = action.payload.expiryTime || null;
        state.success = 'Login successful!';
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = { 
          email: action.payload.email, 
          role: action.payload.userRole, 
          userId: action.payload.userId,
          userName: action.payload.userName,
        };
        state.isAuthenticated = true;
        state.tokenExpiry = action.payload.expiryTime || null;
        state.success = 'OTP verified successfully!';
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'OTP verification failed';
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
        state.success = 'Registration successful! Please login with your credentials.';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Registration failed';
      });
  },
});

export const { clearAuthMessages, logout, checkTokenExpiry, refreshTokenExpiry } = authSlice.actions;
export default authSlice.reducer;